import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { RestrictedContent } from '@/components/common/RestrictedContent';
import { MainLayout } from '@/components/layout/MainLayout';

// Lazy loading de páginas
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Dashboard
const DashboardGeneralPage = lazy(() => import('@/pages/dashboard/DashboardGeneralPage'));
const DashboardCuadraturaPage = lazy(() => import('@/pages/dashboard/DashboardCuadraturaPage'));
const DashboardSuscriptoresPage = lazy(() => import('@/pages/dashboard/DashboardSuscriptoresPage'));
const DashboardFinancieroPage = lazy(() => import('@/pages/dashboard/DashboardFinancieroPage'));

// Suscriptores (anteriormente Medios de pago)
const VirtualPOSPage = lazy(() => import('@/pages/payment-methods/VirtualPOSPage').then(m => ({ default: m.VirtualPOSPage })));
const VirtualPOSKPIsPage = lazy(() => import('@/pages/payment-methods/VirtualPOSKPIsPage').then(m => ({ default: m.VirtualPOSKPIsPage })));
const PaykuPage = lazy(() => import('@/pages/payment-methods/PaykuPage').then(m => ({ default: m.PaykuPage })));
const PaykuKPIsPage = lazy(() => import('@/pages/payment-methods/PaykuKPIsPage').then(m => ({ default: m.PaykuKPIsPage })));
const MandatosPage = lazy(() => import('@/pages/payment-methods/MandatosPage').then(m => ({ default: m.MandatosPage })));
const MandatosKPIsPage = lazy(() => import('@/pages/payment-methods/MandatosKPIsPage').then(m => ({ default: m.MandatosKPIsPage })));
const MandatosUploadsPage = lazy(() => import('@/pages/payment-methods/MandatosUploadsPage').then(m => ({ default: m.MandatosUploadsPage })));
const DonandoPage = lazy(() => import('@/pages/payment-methods/DonandoPage').then(m => ({ default: m.DonandoPage })));
const CarteraPage = lazy(() => import('@/pages/subscribers/CarteraPage'));
const CarteraCuadraturaPage = lazy(() => import('@/pages/subscribers/CarteraCuadraturaPage'));
const SociosAnalogosPage = lazy(() => import('@/pages/socios/SociosAnalogosPage/SociosAnalogosPage').then(m => ({ default: m.SociosAnalogosPage })));
const DonacionesPage = lazy(() => import('@/pages/donaciones/DonacionesPage'));
const EmailPage = lazy(() => import('@/pages/email/EmailPage'));
const ErpPage = lazy(() => import('@/pages/erp/ErpPage'));

// Control de Gestión
const ForecastPage = lazy(() => import('@/pages/management/ForecastPage'));
const PresupuestoPage = lazy(() => import('@/pages/management/PresupuestoPage'));
const ProyeccionesPage = lazy(() => import('@/pages/management/ProyeccionesPage'));

// Reportes
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));

// Configuración
const UsuariosPage = lazy(() => import('@/pages/settings/UsuariosPage').then(m => ({ default: m.UsuariosPage })));
const IntegrationsPage = lazy(() => import('@/pages/settings/IntegrationsPage'));
const IntegrationLogsPage = lazy(() => import('@/pages/settings/IntegrationLogsPage'));

// Perfil
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));

// Chatbot
const ChatbotPage = lazy(() => import('@/pages/chatbot/ChatbotEntryPage'));

// Mantenedores
const UsuariosMantenantPage = lazy(() => import('@/pages/mantenants/UsuariosPage/UsuariosMantenantPage').then(m => ({ default: m.UsuariosMantenantPage })));
const EstadosMantenantPage = lazy(() => import('@/pages/mantenants/EstadosPage/EstadosMantenantPage').then(m => ({ default: m.EstadosMantenantPage })));
const BancosMantenantPage = lazy(() => import('@/pages/mantenants/BancosPage/BancosMantenantPage').then(m => ({ default: m.BancosMantenantPage })));
const PlanesPage = lazy(() => import('@/pages/admin/PlanesPage'));

export default function AppRoutes() {
  return (
    <Routes>
      {/* Rutas públicas */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Dashboard - Redirect to General */}
      <Route path="/dashboard" element={<Navigate to="/dashboard/general" replace />} />

      {/* Dashboard */}
      <Route
        path="/dashboard/general"
        element={
          <ProtectedRoute menuKey="dashboard.general">
            <MainLayout>
              <DashboardGeneralPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/cuadratura"
        element={
          <ProtectedRoute menuKey="dashboard.general">
            <MainLayout>
              <DashboardCuadraturaPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/suscriptores"
        element={
          <ProtectedRoute menuKey="dashboard.suscriptores">
            <MainLayout>
              <DashboardSuscriptoresPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/financiero"
        element={
          <ProtectedRoute menuKey="dashboard.financiero">
            <MainLayout>
              <DashboardFinancieroPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Suscriptores */}
      <Route
        path="/subscribers/cartera"
        element={
          <ProtectedRoute menuKey="subscribers.cartera">
            <MainLayout>
              <CarteraPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/cartera/cuadratura"
        element={
          <ProtectedRoute menuKey="subscribers.cartera">
            <MainLayout>
              <CarteraCuadraturaPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/virtual-pos"
        element={
          <ProtectedRoute menuKey="subscribers.virtualpos">
            <MainLayout>
              <VirtualPOSPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/virtual-pos/kpis"
        element={
          <ProtectedRoute menuKey="subscribers.virtualpos">
            <MainLayout>
              <VirtualPOSKPIsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/payku"
        element={
          <ProtectedRoute menuKey="subscribers.payku">
            <MainLayout>
              <PaykuPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/payku/kpis"
        element={
          <ProtectedRoute menuKey="subscribers.payku">
            <MainLayout>
              <PaykuKPIsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/mandatos"
        element={
          <ProtectedRoute menuKey="subscribers.mandatos">
            <MainLayout>
              <MandatosPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/mandatos/uploads"
        element={
          <ProtectedRoute menuKey="subscribers.mandatos">
            <MainLayout>
              <MandatosUploadsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/mandatos/kpis"
        element={
          <ProtectedRoute menuKey="subscribers.mandatos">
            <MainLayout>
              <MandatosKPIsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/donando"
        element={
          <ProtectedRoute menuKey="subscribers.donando">
            <MainLayout>
              <DonandoPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscribers/socios-analogos"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SociosAnalogosPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Donaciones — módulo Pro (preview sin licencia) */}
      <Route
        path="/donaciones"
        element={
          <ProtectedRoute menuKey="pro.donaciones">
            <MainLayout>
              <DonacionesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Email — módulo Pro */}
      <Route
        path="/email"
        element={
          <ProtectedRoute menuKey="pro.email">
            <MainLayout>
              <EmailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* ERP Lite — módulo Pro */}
      <Route
        path="/erp"
        element={
          <ProtectedRoute menuKey="product.erp">
            <MainLayout>
              <ErpPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Control de Gestión - Módulos en desarrollo con acceso restringido */}
      <Route
        path="/management/forecast"
        element={
          <ProtectedRoute menuKey="management.forecast">
            <MainLayout>
              <RestrictedContent
                requiredPermissions={["management:beta", "management:forecast"]}
                fallbackTitle="Módulo Forecast en Desarrollo"
                fallbackMessage="El módulo de Forecast está actualmente en fase de desarrollo y pruebas. Solo usuarios autorizados pueden acceder en esta etapa."
              >
                <ForecastPage />
              </RestrictedContent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/management/presupuesto"
        element={
          <ProtectedRoute menuKey="management.presupuesto">
            <MainLayout>
              <RestrictedContent
                requiredPermissions={["management:beta", "management:presupuesto"]}
                fallbackTitle="Módulo Presupuesto en Desarrollo"
                fallbackMessage="El módulo de Presupuesto está actualmente en fase de desarrollo y pruebas. Solo usuarios autorizados pueden acceder en esta etapa."
              >
                <PresupuestoPage />
              </RestrictedContent>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/management/proyecciones"
        element={
          <ProtectedRoute menuKey="management.proyecciones">
            <MainLayout>
              <RestrictedContent
                requiredPermissions={["management:beta", "management:proyecciones"]}
                fallbackTitle="Módulo Proyecciones en Desarrollo"
                fallbackMessage="El módulo de Proyecciones está actualmente en fase de desarrollo y pruebas. Solo usuarios autorizados pueden acceder en esta etapa."
              >
                <ProyeccionesPage />
              </RestrictedContent>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Reportes */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute menuKey="reports">
            <MainLayout>
              <ReportsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Configuración */}
      <Route
        path="/settings/usuarios"
        element={
          <ProtectedRoute menuKey="settings.usuarios">
            <MainLayout>
              <UsuariosPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/integrations"
        element={
          <ProtectedRoute menuKey="settings.integrations">
            <MainLayout>
              <IntegrationsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/integration-logs"
        element={
          <ProtectedRoute menuKey="settings.integration-logs">
            <MainLayout>
              <IntegrationLogsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Perfil de usuario */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProfilePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Chatbot */}
      <Route
        path="/chatbot"
        element={
          <ProtectedRoute menuKey="pro.chatbot">
            <MainLayout>
              <ChatbotPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Mantenedores */}
      <Route
        path="/mantenants/usuarios"
        element={
          <ProtectedRoute menuKey="mantenants.usuarios">
            <MainLayout>
              <UsuariosMantenantPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mantenants/estados"
        element={
          <ProtectedRoute menuKey="mantenants.estados">
            <MainLayout>
              <EstadosMantenantPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mantenants/bancos"
        element={
          <ProtectedRoute menuKey="mantenants.bancos">
            <MainLayout>
              <BancosMantenantPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/tenants"
        element={<Navigate to="/admin/planes" replace />}
      />
      <Route
        path="/admin/planes"
        element={
          <ProtectedRoute superAdminOnly>
            <MainLayout>
              <PlanesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Redirecciones y 404 */}
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
